/**
 * The app's auth surface, backed by Better Auth. Everything that needs a
 * session, a user, or a sign-in affordance imports it from here — ~85 modules
 * across src/.
 *
 * The API shape — useAuth, useUser, SignIn, SignInButton, SignUpButton,
 * SignOutButton, plus the flat `publicMetadata` user — the API shape matches
 * the original auth surface; keeping the shape is what let those 85
 * modules stay unchanged. `@flashka/shared/credits` and the API still read
 * that metadata shape, so it is load-bearing, not vestigial.
 *
 * Copy is translated via the `auth` i18n namespace (see
 * src/locales/*\/*-auth.json). SignIn renders the real AuthCard inline;
 * SignInButton/SignUpButton open it as a modal via the authDialogs store,
 * useAuthControls().openSignUp opens that same modal imperatively, and
 * useAuthControls().openUserProfile opens the account-management dialog — all
 * hosted once by AuthDialogs, mounted from AuthProvider.
 *
 * IMPORTANT: read these exports inside a component or hook body, never at
 * module scope. This module's own graph reaches back into it (AuthCard →
 * ThemedLogo → settings/theme → use-local-storage), so during evaluation the
 * cycle leaves the bindings in their temporal dead zone — a module-scope read
 * throws at boot.
 */
import { sessionCheck } from '@flashka/shared/auth/session-check';
import { sync } from '@flashka/shared/sync-client/sync';

import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { unregisterWebPushBeforeSignOut } from '@/lib/push/web-push';

import { installAmbientAuthFetch } from './ambient-auth-fetch';
import {
  authClient,
  AVATAR_ENDPOINT,
  clearJwtCache,
  clearSessionToken,
  getJwt,
  getSessionToken,
} from './client';
import { AuthCard } from './components/auth-card';
import { AuthDialogs } from './components/auth-dialogs';
import { ImpersonationBanner } from './components/impersonation-banner';
import { authDialogs } from './store';

// Module scope: a NO-OP unless a session token was planted (impersonation,
// e2e), in which case /api/* has to carry that identity as a Bearer rather
// than fall back to the browser's own cookie. Import time, not a hook — the
// wrapper must be in place before the first /api/* call site runs.
installAmbientAuthFetch();

// ── session mapping ─────────────────────────────────────────────────────

type SessionData = ReturnType<typeof authClient.useSession>['data'];

export interface AuthEmail {
  id: string;
  emailAddress: string;
}

/** The fields `AuthUser.update()` accepts. Anything else is rejected. */
export interface AuthUserUpdate {
  firstName?: string;
  lastName?: string;
  username?: string;
}

export interface AuthUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  username: string | null;
  imageUrl: string;
  hasImage: boolean;
  createdAt: Date | null;
  primaryEmailAddress: AuthEmail | null;
  emailAddresses: AuthEmail[];
  publicMetadata: Record<string, unknown>;
  reload: () => Promise<void>;
  update: (fields: AuthUserUpdate) => Promise<void>;
  setProfileImage: (input: { file: File }) => Promise<void>;
}

/**
 * A rejected identity write. `code` carries Better Auth's STABLE error code
 * when the API supplied one — `USERNAME_IS_ALREADY_TAKEN`, `USERNAME_TOO_SHORT`,
 * `USERNAME_TOO_LONG`, `INVALID_USERNAME` from the username plugin — so callers
 * can message on the specific failure instead of a generic "couldn't save".
 * complete-profile-dialog.tsx maps those codes to translated copy.
 */
export class AuthIdentityError extends Error {
  readonly code: string | null;
  constructor(code: string | null | undefined, message: string) {
    super(message);
    this.name = 'AuthIdentityError';
    this.code = code ?? null;
  }
}

/**
 * Upload an avatar to the worker, which stores it in R2, sets `user.image`
 * ITSELF and answers `{ url }` — so there is deliberately no follow-up
 * `updateUser({ image })` here.
 *
 * Auth: this POSTs same-origin (AVATAR_ENDPOINT is relative), so the
 * first-party session cookie rides along on its own. The Bearer header is
 * added when a session token happens to be planted in localStorage —
 * impersonation or an e2e-seeded session — mirroring what the auth client
 * sends, so this call authenticates as the same identity. `auth-avatar.ts`
 * forwards both onward.
 */
async function uploadAvatar(file: File): Promise<void> {
  const body = new FormData();
  // Field name fixed by the worker contract.
  body.append('file', file);
  const token = getSessionToken();
  const res = await fetch(AVATAR_ENDPOINT, {
    method: 'POST',
    body,
    // Never set content-type by hand — the browser has to append the
    // multipart boundary it generated for `body`.
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    credentials: 'include',
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new AuthIdentityError(
      // 413 is the size cap, 415 the type allowlist — both are the worker
      // saying "not this file", which callers show as a picture error.
      res.status === 413 ? 'AVATAR_TOO_LARGE' : res.status === 415 ? 'AVATAR_TYPE' : null,
      `[auth] avatar upload failed (${res.status}) ${detail}`.trim(),
    );
  }
  // The worker refreshed the stored row; the local session store has no idea.
  // Refetch (cache-bypassing) so every useSession consumer sees the new image.
  await authClient.getSession({ query: { disableCookieCache: true } });
}

export function mapUser(data: NonNullable<SessionData>): AuthUser {
  const u = data.user as typeof data.user & {
    // The username plugin's pair: `username` is the lowercased unique key,
    // `displayUsername` the case-preserving one the user typed. Both optional
    // so a session minted before the plugin landed still maps.
    username?: string | null;
    displayUsername?: string | null;
    subStatus?: string | null;
    subscriptionLength?: string | null;
    isTrialing?: boolean | null;
    partnerSource?: string | null;
    partnerStatus?: string | null;
  };
  const name = u.name ?? '';
  const [firstName, ...rest] = name.split(' ');
  const email: AuthEmail = { id: `${u.id}_email`, emailAddress: u.email };
  return {
    id: u.id,
    firstName: firstName || null,
    lastName: rest.length ? rest.join(' ') : null,
    fullName: name || null,
    // Prefer the case-preserving one — it is what the user typed and what the
    // profile is displayed under; `username` is the normalized unique key.
    username: u.displayUsername || u.username || null,
    imageUrl: u.image ?? '',
    hasImage: Boolean(u.image),
    createdAt: u.createdAt ? new Date(u.createdAt) : null,
    primaryEmailAddress: email,
    emailAddresses: [email],
    // Flat `publicMetadata` bag (incl. the historical `subscriptionLenght`
    // typo) — the shape `@flashka/shared/credits` and the API both read.
    publicMetadata: {
      status: u.subStatus ?? undefined,
      subscriptionLenght: u.subscriptionLength ?? undefined,
      isTrialing: u.isTrialing ?? undefined,
      partnerSource: u.partnerSource ?? undefined,
      partnerStatus: u.partnerStatus ?? undefined,
    },
    // Re-fetch the session (bypassing any cache) so refreshed metadata
    // propagates to every useSession consumer. Post-purchase this happens on
    // its own — see PlanSessionSync below.
    reload: async () => {
      // Identity, not billing: the cached 15-min JWT is a snapshot of the user
      // row taken when it was minted, and it OUTRANKS the cookie on every
      // `/api` call (server/utils/auth.ts), so leaving it in place means the
      // API keeps reading the pre-reload identity and plan claims — checkout
      // identity, partner status — for up to the token's TTL. Metered
      // routes are no longer at risk: since 9c840a1 the tier authority is the
      // user's UserDo, not the JWT claims.
      clearJwtCache();
      await authClient.getSession({ query: { disableCookieCache: true } });
    },
    // The worker stores one `name`, so first/last are joined back together.
    // `username` goes through the same endpoint — better-auth's username
    // plugin owns the column, validates charset/length and uniqueness, and
    // reports a taken handle as `USERNAME_IS_ALREADY_TAKEN`.
    //
    // Bearer note (hygiene, not correctness — since flashka-auth PR #60): the
    // worker now exempts a Bearer caller's own unchanged username, so
    // resubmitting the whole form succeeds and casing-only edits work over
    // Bearer. Diffing before sending (complete-profile-dialog does) is still
    // good practice — it skips a pointless write — but a stale form can no
    // longer 400 the whole update. History: flashka-auth#56.
    update: async ({ firstName, lastName, username }) => {
      const fields: { name?: string; username?: string; displayUsername?: string } = {};
      if (username !== undefined) {
        fields.username = username;
        // The worker derives displayUsername when absent (flashka-auth#59),
        // but sending both explicitly stays the ideal shape: explicit wins,
        // identical value, and no reliance on derivation heuristics.
        fields.displayUsername = username;
      }
      if (firstName !== undefined || lastName !== undefined) {
        fields.name = [firstName ?? '', lastName ?? ''].join(' ').trim();
      }
      if (Object.keys(fields).length === 0) return;
      const { error } = await authClient.updateUser(fields);
      // The `/update-user` response drives better-auth's own session refetch,
      // so the mapped user catches up without an explicit reload here.
      if (error) {
        throw new AuthIdentityError(
          error.code,
          error.message ?? 'identity update failed',
        );
      }
    },
    setProfileImage: ({ file }) => uploadAvatar(file),
  };
}

// `data?.user` (not just `data`): a session payload can arrive without its
// user half — observed in the wild as a hard render crash on `data.user.id`.
// Treat it as signed-out; the next session refetch self-corrects.
function useMappedUser(): { isLoaded: boolean; user: AuthUser | null } {
  const { data, isLoaded } = useSessionState();
  const user = useMemo(() => (data?.user ? mapUser(data) : null), [data]);
  return { isLoaded, user };
}

// better-auth re-enters `isPending` on every session refetch (any auth
// mutation triggers one). Latch `isLoaded`, or hosts gating on it unmount the
// AuthCard mid-flow — sign-up's refetch wiped the OTP step.
function useSessionState() {
  const { data, isPending, error, refetch } = authClient.useSession();
  const [hasResolved, setHasResolved] = useState(!isPending);
  useEffect(() => {
    if (!isPending) setHasResolved(true);
  }, [isPending]);
  return { data, error, refetch, isLoaded: hasResolved };
}

async function signOutEverywhere(): Promise<void> {
  try {
    // While the session still exists: a browser's push token outlives it, so
    // one left registered would keep delivering this account's pushes to
    // whoever uses the browser next. Bounded — sign-out never waits on it.
    await unregisterWebPushBeforeSignOut();
    await authClient.signOut();
  } finally {
    clearSessionToken();
    window.location.assign('/');
  }
}

// ── hooks ───────────────────────────────────────────────────────────────

/**
 * Is this session an admin's impersonation (`impersonatedBy`)? The worker
 * declares the field server-side (`session.additionalFields`, `input: false`),
 * so it arrives on get-session but not in the client's inferred type — the
 * same narrow read as `ImpersonationBanner`.
 */
function isImpersonatedSession(data: SessionData): boolean {
  const by = (data?.session as { impersonatedBy?: unknown } | undefined)?.impersonatedBy;
  return typeof by === 'string' && by.length > 0;
}

export function useAuth() {
  const { data, error, refetch, isLoaded } = useSessionState();
  return {
    isLoaded,
    // user/session guarded like useMappedUser: never crash on a partial payload
    isSignedIn: Boolean(data?.user),
    /**
     * `isSignedIn` is false for TWO different things: a visitor the server
     * says has no session, and a session check that never got an answer
     * (offline, the auth worker down, a 5xx). `'unknown'` is the second, and a
     * surface that would otherwise tell a signed-out visitor something
     * definitive — "this deck does not exist" — must not say it then.
     */
    session: sessionCheck({ hasUser: Boolean(data?.user), error }),
    /** Ask the session store again — the retry for `session === 'unknown'`. */
    recheckSession: () => {
      void refetch();
    },
    userId: data?.user?.id ?? null,
    sessionId: data?.session?.id ?? null,
    /** An admin driving this user's session — never this browser's own. */
    isImpersonating: isImpersonatedSession(data),
    orgId: null,
    getToken: async (_options?: unknown) => getJwt(),
    signOut: signOutEverywhere,
  };
}

export function useUser() {
  const { isLoaded, user } = useMappedUser();
  return { isLoaded, isSignedIn: Boolean(user), user };
}

/**
 * Post-auth redirect targets, accepted everywhere the app already passes them
 * and DROPPED everywhere: auth happens in a modal that leaves the page in
 * place, and the host re-renders signed-in once the session flips. Kept in the
 * signatures because ~10 call sites still spell out where they'd want to land.
 */
interface RedirectProps {
  forceRedirectUrl?: string | null;
  fallbackRedirectUrl?: string | null;
  signInForceRedirectUrl?: string | null;
  signInFallbackRedirectUrl?: string | null;
  signUpForceRedirectUrl?: string | null;
  signUpFallbackRedirectUrl?: string | null;
}

/** Imperative counterparts to the button components below. */
export function useAuthControls() {
  const { user } = useMappedUser();
  return {
    signOut: signOutEverywhere,
    user,
    openUserProfile: () => authDialogs.openUserProfile(),
    openSignUp: (_options?: RedirectProps) => authDialogs.openSignUp(),
  };
}

// ── components ──────────────────────────────────────────────────────────

/**
 * Keeps the session-backed `useUser()` fields in step with the synced `plan`
 * entity. The plan is pushed to the UserDo by the auth worker the moment a
 * billing webhook lands and broadcast over sync, so a change here IS the
 * "purchase landed" signal — one cache-bypassing session refetch then
 * refreshes `publicMetadata` (analytics on upgrade-success reads it) and drops
 * the cached JWT. No polling: plan-gated UI already reads `usePlan()`.
 */
function PlanSessionSync() {
  const plan = sync.plan.use();
  const seen = useRef<typeof plan>(undefined);
  useEffect(() => {
    const previous = seen.current;
    seen.current = plan;
    // First value is the initial hydrate, not a change.
    if (previous === undefined || plan === undefined || previous === plan) return;
    clearJwtCache();
    void authClient.getSession({ query: { disableCookieCache: true } });
  }, [plan]);
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  return (
    <>
      {children}
      <PlanSessionSync />
      <AuthDialogs />
      {/* Renders nothing unless the resolved session carries `impersonatedBy`. */}
      <ImpersonationBanner />
    </>
  );
}

// These open the AuthCard as a real modal (AuthDialogs, hosted by
// AuthProvider) via the authDialogs store. Children are often full <Button>
// components already, so these wrap in a span with button semantics
// (role/tabIndex/keys) instead of a real <button>, which would nest
// interactive elements.
function ActionSpan({
  onActivate,
  children,
}: {
  onActivate: () => void;
  children: ReactNode;
}) {
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={onActivate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onActivate();
        }
      }}>
      {children}
    </span>
  );
}

type AuthButtonProps = RedirectProps & { children?: ReactNode; mode?: string };

export function SignInButton({ children }: AuthButtonProps) {
  const { t } = useTranslation();
  return (
    <ActionSpan onActivate={() => authDialogs.openSignIn()}>
      {children ?? t('auth.buttons.signIn')}
    </ActionSpan>
  );
}

export function SignUpButton({ children }: AuthButtonProps) {
  const { t } = useTranslation();
  return (
    <ActionSpan onActivate={() => authDialogs.openSignUp()}>
      {children ?? t('auth.buttons.signUp')}
    </ActionSpan>
  );
}

export function SignOutButton({ children }: { children?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <ActionSpan onActivate={() => void signOutEverywhere()}>
      {children ?? t('auth.buttons.signOut')}
    </ActionSpan>
  );
}

/**
 * Renders the AuthCard inline, as a page rather than a modal. The card is one
 * unified sign-in-or-sign-up flow, so there is no mode to manage. No
 * `onAuthenticated` needed here: the caller is `ProtectedRoute` (App.tsx),
 * which re-renders its children once `useSession`/`useAuth` flips to
 * signed-in.
 */
export function SignIn(_props: RedirectProps) {
  return (
    <div className="flex w-full justify-center">
      <AuthCard />
    </div>
  );
}
